import { CommentAvatar } from "@app/shared/components/Comments/CommentAvatar";
import { useCommentForm } from "@app/shared/hooks";
import { SubmitButton } from "@design-system/components/Button";
import { TextareaInput } from "@design-system/components/Form";
import { trans, plainTrans } from "@mongez/localization";
import { Form, HiddenInput } from "@mongez/react-form";
import { Check, MessageSquareText, SendIcon, X } from "lucide-react";

type CommentFormProps = {
  compact?: boolean;
  type: string;
  typeId: number;
};

export function CommentForm({
  compact = false,
  type,
  typeId,
}: CommentFormProps) {
  const { activeComment, cancelEditing, isEditing, submitComment, user } =
    useCommentForm(typeId, type);

  return (
    <Form
      onSubmit={submitComment}
      id={`comment-form-${activeComment?.id || "new"}`}
      className={
        compact
          ? "rounded-2xl border border-primary/20 bg-white p-3 shadow-sm dark:bg-slate-900"
          : "rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5"
      }
    >
      <HiddenInput name="id" value={activeComment?.id} />

      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {!compact && <CommentAvatar user={user} />}
          <div>
            <p className="flex items-center gap-2 text-sm font-black text-slate-900 dark:text-white">
              <MessageSquareText className="h-4 w-4 text-primary" />
              {isEditing ? trans("editingComment") : trans("shareYourThoughts")}
            </p>
            {!compact && (
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {isEditing
                  ? trans("editingCommentHint")
                  : trans("commentPrompt")}
              </p>
            )}
          </div>
        </div>

        {isEditing && (
          <button
            type="button"
            onClick={cancelEditing}
            className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <X className="h-4 w-4" />
            {trans("cancel")}
          </button>
        )}
      </div>

      <TextareaInput
        required
        autoExpand
        showCount
        maxLength={1000}
        defaultValue={activeComment?.comment}
        name="comment"
        placeholder={plainTrans("writeComment")}
        className="min-h-24 resize-none rounded-2xl border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 shadow-inner transition focus:border-primary/40 focus:bg-white focus:ring-4 focus:ring-primary/10 dark:border-slate-700 dark:bg-slate-800 dark:focus:bg-slate-950"
        rows={compact ? 2 : 3}
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-400 dark:text-slate-500">
          {trans("commentGuideline")}
        </p>
        <SubmitButton size="sm" className="h-10 rounded-full px-5 shadow-sm">
          {isEditing ? (
            <Check className="h-4 w-4" />
          ) : (
            <SendIcon className="h-4 w-4" />
          )}
          <span>
            {isEditing ? trans("saveComment") : trans("publishComment")}
          </span>
        </SubmitButton>
      </div>
    </Form>
  );
}
